import { useCallback, useEffect, useRef, useState } from 'react';
import agent from '../../app/api/agent';
import { fieldChecked, fieldValue, useModalOpen } from '../../app/components/polaris';
import type { DataDeletionMode, DataManagementStatus } from '../../app/models/DataManagement';

const busy = (status?: DataManagementStatus) => ['Queued', 'Processing'].includes(status?.latestJob?.state || '');
const title = (mode: DataDeletionMode) => mode === 'all' ? 'Delete all vehicles and fitments' : 'Delete all fitment assignments';

export default function DataManagementSettings({ disabled = false, onExport }: { disabled?: boolean; onExport: () => void }) {
    const [status, setStatus] = useState<DataManagementStatus>();
    const [error, setError] = useState('');
    const [mode, setMode] = useState<DataDeletionMode>();
    const [confirmation, setConfirmation] = useState('');
    const [removeShopifyTags, setRemoveShopifyTags] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const mounted = useRef(true);
    const refreshing = useRef(false);
    const refresh = useCallback(async () => {
        if (refreshing.current) return;
        refreshing.current = true;
        setLoading(true);
        try {
            const next = await agent.Settings.dataManagement();
            if (mounted.current) { setStatus(next); setError(''); }
        } catch {
            if (mounted.current) setError('Unable to load data management. Refresh before deleting data.');
        } finally { refreshing.current = false; if (mounted.current) setLoading(false); }
    }, []);
    useEffect(() => {
        mounted.current = true;
        void refresh();
        return () => { mounted.current = false; };
    }, [refresh]);
    const running = busy(status);
    useEffect(() => {
        if (!running) return;
        const timer = window.setInterval(() => void refresh(), 3000);
        return () => window.clearInterval(timer);
    }, [running, refresh]);

    const open = (selected: DataDeletionMode) => {
        setLoading(true);
        setConfirmation('');
        setRemoveShopifyTags(false);
        setMode(selected);
        void refresh();
    };
    const close = () => { if (!submittingRef.current) { setMode(undefined); setConfirmation(''); } };
    const dialogRef = useModalOpen(!!mode, close);
    const submit = async () => {
        if (!mode || confirmation !== 'DELETE' || loading || submittingRef.current || disabled || !status || error || busy(status) || status.blockers.length) return;
        submittingRef.current = true;
        setSubmitting(true);
        try {
            const job = await agent.Settings.deleteFitmentData(mode, confirmation, removeShopifyTags);
            setStatus({ ...status, latestJob: job });
            setMode(undefined);
            setConfirmation('');
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Unable to start deletion. Refresh to check its status before trying again.');
        } finally { submittingRef.current = false; setSubmitting(false); }
    };
    const blocked = disabled || loading || !!error || !status || busy(status) || status.blockers.length > 0;
    const counts = status?.counts;
    const job = status?.latestJob;
    return <>
        {error && !mode && <s-banner tone="critical">
            {error}
            <s-button slot="secondary-actions" onClick={() => void refresh()} loading={loading}>Refresh status</s-button>
        </s-banner>}
        {job && <s-banner tone={job.state === 'Failed' ? 'critical' : job.state === 'Completed' ? 'success' : 'info'}
            heading={job.state === 'Completed' ? 'Deletion complete' : job.state === 'Failed' ? 'Deletion stopped' : 'Deleting data'}>
            <s-stack gap="small-200">
                <s-paragraph>{job.message}</s-paragraph>
                {job.state !== 'Completed' && <s-paragraph>{job.removeShopifyTags !== false
                    ? 'This deletion includes recognized Shopify vehicle and universal-fit tags. Other product tags are kept.'
                    : 'This deletion keeps Shopify product tags.'}</s-paragraph>}
                {job.error && <s-paragraph>{job.error}</s-paragraph>}
                {busy(status) && <s-progress value={job.progress} max={100} accessibilityLabel="Deletion progress" />}
            </s-stack>
        </s-banner>}
        <s-section heading="Delete data">
            <s-stack gap="base">
                {disabled && <s-banner tone="info">Save or discard your changes before deleting data.</s-banner>}
                <s-grid gridTemplateColumns="1fr auto" alignItems="center" gap="base">
                    <s-text>Delete all fitment assignments</s-text>
                    <s-button tone="critical" onClick={() => open('assignments')} disabled={disabled || running}>Delete assignments…</s-button>
                </s-grid>
                <s-divider />
                <s-grid gridTemplateColumns="1fr auto" alignItems="center" gap="base">
                    <s-text>Delete all vehicles and fitments</s-text>
                    <s-button tone="critical" onClick={() => open('all')} disabled={disabled || running}>Delete vehicles &amp; fitments…</s-button>
                </s-grid>
                <s-divider />
                <s-grid gridTemplateColumns="1fr auto" alignItems="center" gap="base">
                    <s-text>Export a copy first</s-text>
                    <s-button icon="export" onClick={onExport} disabled={disabled}>Export data</s-button>
                </s-grid>
            </s-stack>
        </s-section>
        <s-modal ref={dialogRef} id="pf-delete-data-modal" heading={mode ? `${title(mode)}?` : 'Delete data'}>
            {/* s-modal keeps its children mounted; render the review only while open. */}
            {mode && <s-stack gap="base">
                <s-banner tone="critical">This affects your entire store and cannot be undone in the app.</s-banner>
                {loading && <s-stack direction="inline" gap="small-200" alignItems="center">
                    <s-spinner size="base" accessibilityLabel="Checking your data" /><s-text>Checking your data…</s-text>
                </s-stack>}
                {!loading && !error && counts && <s-stack gap="small-200">
                    <s-heading>Removed</s-heading>
                    <s-unordered-list>
                        {mode === 'all' && <s-list-item>{counts.vehicles.toLocaleString()} vehicle records</s-list-item>}
                        <s-list-item>{counts.productAssignments.toLocaleString()} product assignments</s-list-item>
                        <s-list-item>{counts.variantAssignments.toLocaleString()} variant assignments</s-list-item>
                        <s-list-item>{counts.collectionAssignments.toLocaleString()} collection assignments</s-list-item>
                        <s-list-item>{counts.turn14Links.toLocaleString()} Turn14 product links</s-list-item>
                        <s-list-item>Universal flags in PartFits on {counts.universalProducts.toLocaleString()} products</s-list-item>
                        <s-list-item>Fitment notes</s-list-item>
                        {removeShopifyTags && <s-list-item>Recognized Shopify vehicle and universal-fit tags</s-list-item>}
                    </s-unordered-list>
                    <s-text color="subdued">Kept: {mode === 'assignments' ? 'Your vehicle catalog, ' : ''}Shopify products and collections, {removeShopifyTags ? 'other product tags' : 'all Shopify product tags'}, selector fields, and app settings.</s-text>
                    {!removeShopifyTags && <s-text color="subdued">Kept vehicle and universal-fit tags may still drive tag-based storefront filtering.</s-text>}
                </s-stack>}
                <s-checkbox label="Also remove vehicle-fitment tags from Shopify products" checked={removeShopifyTags}
                    disabled={blocked || submitting} onChange={event => setRemoveShopifyTags(fieldChecked(event))} />
                {!loading && error && <s-banner tone="critical">
                    {error}
                    <s-button slot="secondary-actions" onClick={() => { setConfirmation(''); void refresh(); }}>Try again</s-button>
                </s-banner>}
                {disabled && <s-banner tone="info">Save or discard your changes before deleting data.</s-banner>}
                {!loading && !!status?.blockers.length && <s-banner tone="warning" heading="Before deleting data">
                    <s-unordered-list>{status.blockers.map(item => <s-list-item key={item}>{item}</s-list-item>)}</s-unordered-list>
                    <s-button slot="secondary-actions" onClick={() => { setConfirmation(''); void refresh(); }}>Refresh status</s-button>
                </s-banner>}
                {!loading && running && <s-banner tone="info">A deletion is already queued or running.</s-banner>}
                <s-text-field label="Type DELETE to confirm" value={confirmation} autocomplete="off" disabled={blocked || submitting}
                    onInput={event => setConfirmation(fieldValue(event))} />
            </s-stack>}
            <s-button slot="primary-action" variant="primary" tone="critical" loading={submitting}
                disabled={blocked || confirmation !== 'DELETE'} onClick={submit}>
                {mode === 'all' ? 'Delete vehicles & fitments' : 'Delete assignments'}
            </s-button>
            <s-button slot="secondary-actions" disabled={submitting} onClick={close}>Cancel</s-button>
        </s-modal>
    </>;
}
